import { useEffect, useRef, useState } from "react";
import type { MockupSize } from "../../components/swagstore/mockups/mockupGeneratorUtils";

export type MockupGeneratorTab = "generate" | "exports" | "upload";

/**
 * Tab visibility, controlled-tab sync, open reset, and post-generate auto-switch.
 */
export function useMockupGeneratorTabs({
  open,
  productId,
  initialTab,
  controlledTab,
  editMode,
  hideGeneratedMockupsTab,
  embedded,
  generatingMockup,
  savedMockupsCount,
  hasMockupOutput,
}: {
  open: boolean;
  productId?: number;
  initialTab: MockupGeneratorTab;
  controlledTab?: MockupGeneratorTab;
  editMode: boolean;
  hideGeneratedMockupsTab: boolean;
  embedded: boolean;
  generatingMockup: boolean;
  savedMockupsCount: number;
  hasMockupOutput: boolean;
  preferredSize?: MockupSize;
}) {
  const shouldAutoSwitchToExportsRef = useRef(false);
  const [activeTab, setActiveTab] = useState<MockupGeneratorTab>(initialTab);
  const [manualUploadFiles, setManualUploadFiles] = useState<File[]>([]);

  useEffect(() => {
    setActiveTab(editMode ? "upload" : initialTab);
    setManualUploadFiles([]);
    shouldAutoSwitchToExportsRef.current = false;
  }, [open, productId, editMode, initialTab]);

  useEffect(() => {
    if (!shouldAutoSwitchToExportsRef.current || editMode || hideGeneratedMockupsTab) return;
    if (generatingMockup) return;
    if (!(savedMockupsCount > 0 || hasMockupOutput)) return;
    setActiveTab("exports");
    shouldAutoSwitchToExportsRef.current = false;
  }, [
    editMode,
    generatingMockup,
    hideGeneratedMockupsTab,
    savedMockupsCount,
    hasMockupOutput,
  ]);

  useEffect(() => {
    if (controlledTab) setActiveTab(controlledTab);
  }, [controlledTab]);

  const showUploadTab = true;
  const showGenerateTab = true;
  const showExportsTab = !hideGeneratedMockupsTab && !editMode;
  const showTabs =
    (showGenerateTab ? 1 : 0) + (showUploadTab ? 1 : 0) + (showExportsTab ? 1 : 0) > 1;
  const resolvedTab = controlledTab ?? activeTab;
  const showUploadContent = showUploadTab && resolvedTab === "upload";
  const showGenerateContent = showGenerateTab && resolvedTab === "generate";
  const isCompactUploadDialog =
    showUploadContent && !editMode && (hideGeneratedMockupsTab || embedded);

  const markAutoSwitchToExports = () => {
    shouldAutoSwitchToExportsRef.current = true;
  };

  return {
    activeTab,
    setActiveTab,
    manualUploadFiles,
    setManualUploadFiles,
    showUploadTab,
    showGenerateTab,
    showExportsTab,
    showTabs,
    resolvedTab,
    showUploadContent,
    showGenerateContent,
    isCompactUploadDialog,
    markAutoSwitchToExports,
  };
}
